import { PROVIDER_API, USE_MOCK_AUTH, caseSlaPath } from "../config/api";
import { daysLateFromDueDate } from "../utils/calendarDate";
import { displayPersonName } from "../utils/personName";
import { formatGhanaPhoneLocal } from "../utils/ghanaPhone";
import { apiRequest, unwrapApiEnvelope } from "./apiClient";

function toNumber(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

export function normalizeCase(raw) {
  if (!raw || typeof raw !== "object") return null;

  const nested = raw.customer && typeof raw.customer === "object" ? raw.customer : null;
  const amountOwed = toNumber(raw.amount_owed ?? raw.loan_amount ?? raw.amountOwed ?? raw.outstanding_balance);
  const amountPaid = toNumber(raw.amount_paid ?? raw.amountPaid ?? 0);
  const outstandingAmount =
    raw.outstanding != null && raw.outstanding !== ""
      ? toNumber(raw.outstanding)
      : toNumber(raw.outstanding_balance ?? Math.max(0, amountOwed - amountPaid));
  const customer = displayPersonName(
    { first_name: raw.first_name || nested?.first_name, last_name: raw.last_name || nested?.last_name },
    raw.fullname,
    raw.customer_name,
    nested?.fullname,
    nested?.display_name,
    typeof raw.customer === "string" ? raw.customer : "",
    raw.customerName
  );
  const caseId = raw.case_code || raw.case_id || raw.caseId || (raw.id != null ? String(raw.id) : "");
  const status = raw.status || "open";
  const escalated = Boolean(
    raw.escalated ??
      raw.is_escalated ??
      (typeof status === "string" && status.toLowerCase().includes("escalat"))
  );
  const dueDate = raw.loan_due_date || raw.due_date || raw.dueDate || null;
  const daysFromApi = raw.days_overdue ?? raw.daysOverdue;
  const daysOverdue =
    daysFromApi == null || daysFromApi === ""
      ? daysLateFromDueDate(dueDate)
      : toNumber(daysFromApi);

  return {
    id: caseId || String(raw.id || ""),
    numericId: raw.id != null && Number.isFinite(Number(raw.id)) ? Number(raw.id) : null,
    caseId,
    accountNumber: raw.account_number || raw.accountNumber || "",
    first_name: raw.first_name || nested?.first_name || "",
    last_name: raw.last_name || nested?.last_name || "",
    fullname: raw.fullname || nested?.fullname || "",
    customer_name: customer,
    customer,
    customerNumber: formatGhanaPhoneLocal(
      raw.customer_number || raw.customerNumber || nested?.customer_number || ""
    ),
    phone: formatGhanaPhoneLocal(raw.phone_number || raw.phone || nested?.phone_number || ""),
    email: raw.email || nested?.email || "",
    ghana_card_number: raw.ghana_card_number || nested?.ghanacard_id || "",
    gender: raw.gender || nested?.gender || "",
    date_of_birth: raw.date_of_birth || nested?.date_of_birth || null,
    country: raw.country || nested?.country || "",
    exact_area: raw.exact_area || nested?.location || "",
    gps_address: raw.gps_address || nested?.gps_address || "",
    loan_amount: toNumber(raw.loan_amount ?? nested?.loan_amount),
    last_payment_date: raw.last_payment_date || nested?.last_payment_date || null,
    region: raw.region || nested?.region || "",
    amountOwed,
    amountPaid,
    outstandingAmount,
    dueDate,
    daysOverdue,
    status,
    escalated,
    portfolioSlug: raw.portfolio_slug || null,
    portfolioGroupSlug: raw.portfolio_group_slug || null,
    providerId: raw.provider_id ?? raw.provider?.id ?? raw.provider?.provider_id ?? null,
    providerSlug: raw.provider_id != null ? String(raw.provider_id) : null,
    institution: raw.provider?.name || raw.provider_name || null,
    updatedAt: raw.updated_at || raw.created_at || null,
    createdAt: raw.created_at || null,
  };
}

function extractCases(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.cases)) return data.cases;
  if (Array.isArray(data?.data)) return data.data;
  return [];
}

const LIST_PAGE_LIMIT = 500;
const LIST_TTL_MS = 60_000;

let casesInFlight = { token: null, promise: null };
let casesAllCache = { token: null, promise: null, result: null, at: 0 };
let summaryCache = { token: null, promise: null, result: null, at: 0 };

/**
 * GET /api/provs/cases
 */
export async function fetchProviderCases(token, params = {}, { force = false } = {}) {
  if (USE_MOCK_AUTH || !token) return null;

  const query = new URLSearchParams();
  if (params.page) query.set("page", String(params.page));
  if (params.limit || params.per_page) {
    query.set("limit", String(params.limit || params.per_page));
  }
  if (params.sort_order) query.set("sort_order", params.sort_order);
  if (params.search) query.set("search", params.search);

  const cacheKey = `${token}?${query.toString()}`;
  if (!force && casesInFlight.token === cacheKey && casesInFlight.promise) {
    return casesInFlight.promise;
  }

  const promise = (async () => {
    const path = query.toString()
      ? `${PROVIDER_API.cases}?${query.toString()}`
      : PROVIDER_API.cases;
    const json = await apiRequest(path, { method: "GET", token });
    const data = unwrapApiEnvelope(json);
    return {
      cases: extractCases(data).map(normalizeCase).filter(Boolean),
      pagination: data?.pagination || null,
    };
  })();

  casesInFlight = { token: cacheKey, promise };
  return promise;
}

/** Fetch every page so client-side filters still work. */
export async function fetchAllProviderCases(token, { force = false } = {}) {
  if (USE_MOCK_AUTH || !token) return [];

  if (
    !force &&
    casesAllCache.token === token &&
    casesAllCache.result &&
    Date.now() - casesAllCache.at < LIST_TTL_MS
  ) {
    return casesAllCache.result;
  }
  if (!force && casesAllCache.token === token && casesAllCache.promise) {
    return casesAllCache.promise;
  }

  const promise = (async () => {
    const first = await fetchProviderCases(
      token,
      { page: 1, limit: LIST_PAGE_LIMIT, sort_order: "desc" },
      { force: true }
    );
    if (!first) return [];

    const lastPage = Number(first.pagination?.last_page || 1);
    const rows =
      lastPage <= 1
        ? first.cases
        : first.cases.concat(
            (
              await Promise.all(
                Array.from({ length: lastPage - 1 }, (_, i) =>
                  fetchProviderCases(
                    token,
                    { page: i + 2, limit: LIST_PAGE_LIMIT, sort_order: "desc" },
                    { force: true }
                  )
                )
              )
            ).flatMap((page) => page?.cases || [])
          );

    casesAllCache = { token, promise: null, result: rows, at: Date.now() };
    return rows;
  })();

  casesAllCache = { token, promise, result: casesAllCache.result, at: casesAllCache.at };
  return promise;
}

export const EMPTY_CASES_SUMMARY = {
  totalCases: 0,
  totalOwed: 0,
  totalPaid: 0,
  totalEscalated: 0,
};

export function normalizeCasesSummary(raw) {
  if (!raw || typeof raw !== "object") return { ...EMPTY_CASES_SUMMARY };
  return {
    totalCases: toNumber(
      raw.total_cases ?? raw.total_accounts ?? raw.totalAccounts ?? raw.cases
    ),
    totalOwed: toNumber(raw.total_owed ?? raw.totalOwed),
    totalPaid: toNumber(raw.total_paid ?? raw.totalPaid),
    totalEscalated: toNumber(
      raw.total_escalated ??
        raw.escalated_cases ??
        raw.escalated ??
        raw.high_risk_classifications
    ),
  };
}

/**
 * GET /api/provs/cases/summary
 */
export async function fetchCasesSummary(token, { force = false } = {}) {
  if (USE_MOCK_AUTH || !token) return null;

  if (
    !force &&
    summaryCache.token === token &&
    summaryCache.result &&
    Date.now() - summaryCache.at < LIST_TTL_MS
  ) {
    return summaryCache.result;
  }
  if (!force && summaryCache.token === token && summaryCache.promise) {
    return summaryCache.promise;
  }

  const promise = (async () => {
    const json = await apiRequest(PROVIDER_API.casesSummary, {
      method: "GET",
      token,
    });
    const data = unwrapApiEnvelope(json);
    const result = normalizeCasesSummary(data);
    summaryCache = { token, promise: null, result, at: Date.now() };
    return result;
  })();

  summaryCache = { token, promise, result: summaryCache.result, at: summaryCache.at };
  return promise;
}

/**
 * GET /api/provs/cases/{case_id}/sla
 */
export async function fetchProviderCaseSla(token, caseId) {
  if (USE_MOCK_AUTH || !token || !caseId) return null;
  const json = await apiRequest(caseSlaPath(caseId), { method: "GET", token });
  return unwrapApiEnvelope(json);
}

export function clearProviderCasesCache() {
  casesInFlight = { token: null, promise: null };
  casesAllCache = { token: null, promise: null, result: null, at: 0 };
  summaryCache = { token: null, promise: null, result: null, at: 0 };
}
